// src/pages/ListaFraccionamientos.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, getCountFromServer, where } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import PlatformBackButton from '../components/PlatformBackButton';
import { FaUsers } from 'react-icons/fa';
import './ListaFraccionamientos.css';

const normalizePlanKey = (value = '') => {
    const base = value.toString().trim().toLowerCase();
    if (!base) {
        return '';
    }
    return base
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '');
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(numeric);
};

const ListaFraccionamientos = () => {
    const [fraccionamientos, setFraccionamientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [planPrices, setPlanPrices] = useState({});
    const [houseCounts, setHouseCounts] = useState({});
    const [requestedHouseCounts, setRequestedHouseCounts] = useState({});
    const [selectedFraccUsuarios, setSelectedFraccUsuarios] = useState('');
    const [usuariosFracc, setUsuariosFracc] = useState([]);
    const [usuariosLoading, setUsuariosLoading] = useState(false);
    const [usuariosError, setUsuariosError] = useState('');
    const navigate = useNavigate();
    const { userData: user, loading: authLoading } = useAuth();

    // Función para calcular los días restantes de la suscripción
    const calcularEstadoVencimiento = (fechaVencimiento) => {
        if (!fechaVencimiento || typeof fechaVencimiento.toDate !== 'function') {
            return { diffDays: null, className: '' };
        }

        const hoy = new Date();
        const vencimiento = fechaVencimiento.toDate();
        hoy.setHours(0, 0, 0, 0);

        const diffTime = vencimiento.getTime() - hoy.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { diffDays, className: 'expirado' };
        }
        if (diffDays <= 7) {
            return { diffDays, className: 'expirando' };
        }
        return { diffDays, className: '' };
    };

    const resolvePlanPrice = (fracc, priceMap) => {
        if (!fracc) {
            return null;
        }

        const explicitCandidate = ['precio', 'precioPlan', 'planPrecio', 'costoPlan', 'costoMensual', 'tarifa']
            .map((key) => fracc[key])
            .find((value) => Number.isFinite(Number(value)));

        if (explicitCandidate !== undefined && explicitCandidate !== null) {
            const numeric = Number(explicitCandidate);
            return Number.isFinite(numeric) ? numeric : null;
        }

        const normalizedKey = normalizePlanKey(fracc.suscripcion);
        if (!normalizedKey) {
            return null;
        }

        const matchedPrice = priceMap[normalizedKey];
        return Number.isFinite(Number(matchedPrice)) ? Number(matchedPrice) : null;
    };

    const generarClase = (prefijo, valor) => {
        if (!valor) {
            return `${prefijo}-default`;
        }

        const normalizado = valor
            .toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-');

        return `${prefijo}-${normalizado}`;
    };

    useEffect(() => {
        // Si el contexto de autenticación todavía está cargando, no hagas NADA.
        // El useEffect se volverá a ejecutar cuando authLoading cambie a false.
        if (authLoading) {
            return; 
        }

        // Si la autenticación terminó pero no hay usuario, redirigir 
        if (!user) {
            setLoading(false);
            navigate('/PlatformAdminDashboard'); // O a donde corresponda
            return;
        }
        console.log(`4. Usuario cargado. Su rol es: '${user.rol}'`);

        // --- LÓGICA DE ROLES ---
        if (user.rol === 'platformadmin') {
            const fetchData = async () => {
                try {
                    const fraccQuery = query(collection(db, 'fraccionamientos'), orderBy('fechaCreacion', 'desc'));
                    const [fraccSnapshot, planesSnapshot, solicitudesSnapshot] = await Promise.all([
                        getDocs(fraccQuery),
                        getDocs(collection(db, 'planes_suscripcion')),
                        getDocs(collection(db, 'solicitudes_registro')),
                    ]);

                    const priceMap = planesSnapshot.docs.reduce((acc, docSnap) => {
                        const data = docSnap.data() || {};
                        const key = normalizePlanKey(data.nombre);
                        if (key) {
                            const value = Number(data.costoMensual);
                            if (Number.isFinite(value)) {
                                acc[key] = value;
                            }
                        }
                        return acc;
                    }, {});

                    setPlanPrices(priceMap);

                    const fraccDocs = fraccSnapshot.docs.map((docSnap) => ({
                        id: docSnap.id,
                        data: docSnap.data(),
                    }));

                    const fraccIds = new Set(fraccDocs.map(({ id }) => id));

                    const requestedMap = solicitudesSnapshot.docs.reduce((acc, docSnap) => {
                        const data = docSnap.data() || {};
                        const linkedIdCandidates = [
                            data.fraccionamientoGenerado,
                            data.fraccionamientoId,
                            data.fraccId,
                            data.fraccID,
                            data.fraccionamiento,
                        ];

                        const linkedId = linkedIdCandidates
                            .map((value) => (typeof value === 'string' ? value.trim() : null))
                            .find((value) => value);

                        if (!linkedId || !fraccIds.has(linkedId)) {
                            return acc;
                        }

                        const rawRequested = [
                            data.numLotes,
                            data.numeroLotes,
                            data.numeroCasas,
                            data.totalCasas,
                            data.totalLotes,
                            data.casasSolicitadas,
                        ].find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');

                        const numeric = Number(rawRequested);
                        if (Number.isFinite(numeric) && numeric >= 0) {
                            acc[linkedId] = numeric;
                        }

                        return acc;
                    }, {});

                    setRequestedHouseCounts(requestedMap);

                    const countEntries = await Promise.all(
                        fraccDocs.map(async ({ id }) => {
                            try {
                                const casasRef = collection(db, 'fraccionamientos', id, 'casas');
                                const countSnapshot = await getCountFromServer(casasRef);
                                const rawCount = countSnapshot.data()?.count;
                                const total = Number(rawCount);
                                return [id, Number.isFinite(total) ? total : 0];
                            } catch (countError) {
                                console.error(`Error al obtener total de casas para ${id}:`, countError);
                                return [id, null];
                            }
                        })
                    );

                    setHouseCounts(Object.fromEntries(countEntries));

                    const fraccsList = fraccDocs.map(({ id, data }) => ({
                        id,
                        ...data,
                    }));

                    fraccsList.sort((a, b) => {
                        const fechaA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(0);
                        const fechaB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(0);
                        return fechaB.getTime() - fechaA.getTime();
                    });

                    setFraccionamientos(fraccsList);
                } catch (error) {
                    console.error("Error al obtener lista de fraccionamientos: ", error);
                } finally {
                    setLoading(false); // La carga de datos de la lista ha terminado.
                }
            };
            fetchData();
        } else {
            // Si el rol no es platformadmin, no tiene permiso para ver esta página.
            setLoading(false);
            navigate('/'); // Redirigir al dashboard principal o mostrar error.
        }

    }, [user, authLoading, navigate]);

    const handleSelectFraccUsuarios = async (event) => {
        const fraccId = event.target.value;
        setSelectedFraccUsuarios(fraccId);
        setUsuariosError('');
        setUsuariosFracc([]);

        if (!fraccId) {
            return;
        }

        setUsuariosLoading(true);
        try {
            const usuariosRef = collection(db, 'usuarios');
            const usuariosQuery = query(usuariosRef, where('fraccionamientoId', '==', fraccId));
            const snapshot = await getDocs(usuariosQuery);
            const lista = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
            setUsuariosFracc(lista);
        } catch (err) {
            console.error('No se pudieron cargar los usuarios del fraccionamiento', err);
            setUsuariosError('No pudimos cargar los usuarios de este fraccionamiento. Intenta nuevamente.');
        } finally {
            setUsuariosLoading(false);
        }
    };

    // El JSX no cambia, pero es crucial revisar los logs para saber por qué podría no mostrarse.
    const resumen = useMemo(() => {
        const base = {
            total: fraccionamientos.length,
            activos: 0,
            inactivos: 0,
            expirando: 0,
        };

        fraccionamientos.forEach((fracc) => {
            const estado = (fracc.estado || '').toLowerCase();
            if (estado === 'activo') {
                base.activos += 1;
            } else if (estado === 'inactivo') {
                base.inactivos += 1;
            }

            const diasInfo = calcularEstadoVencimiento(fracc.fechaVencimiento);
            if (diasInfo.className === 'expirando') {
                base.expirando += 1;
            }
        });

        return base;
    }, [fraccionamientos]);

    const metricCards = useMemo(() => ([
        {
            key: 'total',
            label: 'Total registrado',
            value: resumen.total,
        },
        {
            key: 'activos',
            label: 'Activos',
            value: resumen.activos,
        },
        {
            key: 'inactivos',
            label: 'Inactivos',
            value: resumen.inactivos,
        },
        {
            key: 'por-vencer',
            label: 'Por vencer (<=7 días)',
            value: resumen.expirando,
        },
    ]), [resumen]);

    const formatFecha = (timestamp) => {
        if (timestamp?.toDate) {
            const date = timestamp.toDate();
            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        }
        return 'Fecha no disponible';
    };

    if (loading) {
        return (
            <div className="lista-fracc-loading">
                <div className="loader" aria-hidden="true" />
                <p>Cargando fraccionamientos...</p>
            </div>
        );
    }
    
    //JSX 
    return (
    <div className="lista-fracc-page">
        <div className="lista-fracc-surface">
            <div className="lista-fracc-header">
                <div className="header-back-wrapper-zen">
                    <PlatformBackButton to="/" aria-label="Volver" />
                </div>
                <div>
                    <h2>Gestionar Fraccionamientos</h2>
                    <p className="description-zen-mobile-hide">Supervisa los fraccionamientos activos, próximos a expirar y asigna acciones rápidas.</p>
                </div>
            </div>

            <div className="lista-fracc-metrics">
                {metricCards.map((card) => (
                    <div key={card.key} className={`metric-card metric-${card.key}`}>
                        <div className="metric-label">{card.label}</div>
                        <div className="metric-value">{card.value}</div>
                    </div>
                ))}
            </div>

            <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th>Nombre del Fraccionamiento</th>
                        <th>Administrador Asignado</th>
                        <th>Tipo de Suscripción</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {/* 
                      Verificamos si hay fraccionamientos. Si el array está vacío,
                      mostramos una fila con un mensaje. Si no, mapeamos los datos.
                    */}
                    {fraccionamientos.length === 0 ? (
                        <tr>
                            <td colSpan="5" style={{ textAlign: 'center' }}>
                                No hay fraccionamientos registrados todavía.
                            </td>
                        </tr>
                    ) : (
                        fraccionamientos.map(fracc => {
                            // Llamamos a la función para calcular los días restantes
                            const diasInfo = calcularEstadoVencimiento(fracc.fechaVencimiento);
                            const fechaRegistro = formatFecha(fracc.fechaCreacion);
                            const fechaVencimiento = formatFecha(fracc.fechaVencimiento);
                            const suscripcionPrice = resolvePlanPrice(fracc, planPrices);
                            const formattedPrice = formatCurrency(suscripcionPrice);
                            const totalCasas = houseCounts[fracc.id];
                            const requestedRaw = requestedHouseCounts[fracc.id]
                                ?? fracc.numLotes
                                ?? fracc.totalCasasPlaneadas
                                ?? fracc.casasSolicitadas
                                ?? fracc?.solicitudOriginal?.numLotes
                                ?? fracc?.solicitud?.numLotes;

                            const hasRegisteredCount = Number.isFinite(totalCasas);
                            const registeredCount = hasRegisteredCount ? totalCasas : null;
                            const requestedCount = Number.isFinite(Number(requestedRaw)) ? Number(requestedRaw) : null;

                            const shouldDisplayCounts = hasRegisteredCount || requestedCount !== null;
                            const casasLabel = shouldDisplayCounts
                                ? `${registeredCount !== null ? registeredCount : '—'} / ${requestedCount !== null ? requestedCount : '—'}`
                                : null;
                            const casasTitle = 'Casas registradas / solicitadas';
                            
                            return (
                                <tr key={fracc.id}>
                                    {/* Celda para el Nombre y Días Restantes */}
                                    <td data-label="Nombre">
                                        {fracc.nombre}
                                        <div className="fracc-meta">
                                            <span className="fracc-meta-line">Registrado {fechaRegistro}</span>
                                            {fechaVencimiento !== 'Fecha no disponible' && (
                                                <span
                                                    className={`fracc-meta-line fracc-meta-vencimiento ${diasInfo.className ? `fracc-meta-vencimiento--${diasInfo.className}` : ''}`}
                                                >
                                                    Venc. {fechaVencimiento}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    
                                    {/* Celda para el Nombre del Administrador */}
                                    <td data-label="Admin.">
                                        <div className="admin-info">
                                            <span className="admin-name">{fracc.adminNombre || 'No Asignado'}</span>
                                            {fracc.adminEmail && (
                                                <span className="admin-email">{fracc.adminEmail}</span>
                                            )}
                                        </div>
                                    </td>
                                    
                                    {/* Celda para la Suscripción */}
                                    <td data-label="Susc.">
                                        <span className={`status-pill ${generarClase('suscripcion', fracc.suscripcion)}`}>
                                            {fracc.suscripcion || 'N/A'}
                                        </span>
                                        {formattedPrice && (
                                            <div className="plan-price">
                                                {formattedPrice}
                                            </div>
                                        )}
                                    </td>
                                    
                                    {/* Celda para el Estado */}
                                    <td data-label="Estado">
                                        <span className={`status-pill ${generarClase('estado', fracc.estado)}`}>
                                            {fracc.estado || 'Indefinido'}
                                        </span>
                                        {casasLabel && (
                                            <div className="house-count-label" title={casasTitle}>
                                                Casas: {casasLabel}
                                            </div>
                                        )}
                                    </td>
                                    
                                    {/* Celda para las Acciones */}
                                    <td data-label="Acc.">
                                        <Link to={`/fraccionamientos/${fracc.id}`} className="btn-details">
                                            Gestionar
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
        </div>

        {/* Sección de usuarios autenticados movida a la página dedicada: `/plataforma-admin/usuarios-autenticados` */}
    </div>
);
};

export default ListaFraccionamientos;